import {
	TransactionTitle,
	TransactionType,
	TransactionWithSecurityTct,
} from '@dealstack/shared/lib/generated';
import type { UpdateTransactionDto } from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod';

// FE mirror of the server-side interest-pair rules in TransactionService.resolveInterestPairSync /
// maybeSplitInterestOnUpdate — the dialogs here are UX; the server enforces every rule and answers
// 120242-120245 for clients that skip them.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type InterestPairEditPlan =
	| { kind: 'blocked'; title: string; message: string; offerDeletePair: boolean }
	| { kind: 'confirm'; title: string; message: string }
	| { kind: 'splitPreview'; title: string; message: string }
	| { kind: 'mergePreview'; title: string; message: string };

// The form values carry the prisma-client literal unions while the loaded transaction carries the
// generated API enums; both erase to the same strings, so the comparisons below run on `string`.
const BOTH: string = TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE;

/** "type and seller" rather than "type, seller" — the latter reads as a comma splice in a sentence. */
const listFields = (fields: string[]): string =>
	fields.length <= 1
		? fields[0] ?? ''
		: `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}`;

const normalizeInterest = (title: string | null | undefined): string => title ?? BOTH;

const normalizeType = (type: string | null | undefined): string => type ?? TransactionType.TRANSFER;

/**
 * Legs whose date and units must stay identical across the pair (see interestPair.utils.ts,
 * backend). Every pair type qualifies: a transfer pair's counterpart leg exists to fragment the
 * untouched rail at the visible leg's boundary, so it must resize and re-date with it.
 */
const isPairSyncType = (type: string | null | undefined): boolean => {
	const normalized = normalizeType(type);
	return (
		normalized === TransactionType.ISSUE ||
		normalized === TransactionType.TRANSFER ||
		normalized === TransactionType.REDEMPTION ||
		normalized === TransactionType.CANCELLATION
	);
};

const isSplittableType = (type: string | null | undefined): boolean => {
	const normalized = normalizeType(type);
	return (
		normalized === TransactionType.ISSUE ||
		normalized === TransactionType.TRANSFER ||
		normalized === TransactionType.REDEMPTION ||
		normalized === TransactionType.CANCELLATION
	);
};

/**
 * What the server will do when this Both -> single interest edit is confirmed, per type. Kept as a
 * function rather than nested ternaries because the four outcomes are genuinely different operations,
 * not variations on one.
 */
const splitPreviewMessage = (
	typeAfter: string,
	interestAfter: string,
	cost: unknown,
	t: TranslateFn
): string => {
	if (typeAfter === TransactionType.ISSUE) {
		return t('transactions.interest_pair.split_preview.issue_message');
	}

	if (typeAfter !== TransactionType.TRANSFER) {
		return t('transactions.interest_pair.split_preview.redemption_message');
	}

	// CAP-1999: moving bare legal title to the buyer clears the consideration server-side
	// (maybeSplitInterestOnUpdate) — it was paid for the whole holding, not for legal title alone.
	// Disclose it here so the entered value doesn't just vanish.
	const clearsConsideration = interestAfter === TransactionTitle.LEGAL_TITLE && Number(cost) > 0;
	if (clearsConsideration) {
		return t('transactions.interest_pair.split_preview.transfer_message_legal');
	}

	return t('transactions.interest_pair.split_preview.transfer_message');
};

/**
 * What the server does when a paired leg takes both interests: the linked leg is deleted and this
 * row carries both rails. A seller-to-seller counterpart moves nothing of its own, so folding it in
 * simply restores the transaction as it was before the split.
 */
const mergePreviewMessage = (siblingIsSelf: boolean, t: TranslateFn): string => {
	if (siblingIsSelf) {
		return t('transactions.interest_pair.merge_preview.self_message');
	}
	return t('transactions.interest_pair.merge_preview.message');
};

export const resolveInterestPairEditPlan = (
	transaction: TransactionWithSecurityTct | undefined,
	formValues: UpdateTransactionDto,
	t: TranslateFn
): InterestPairEditPlan | null => {
	if (!transaction) {
		return null;
	}

	const interestBefore = normalizeInterest(transaction.title);
	const interestAfter = normalizeInterest(formValues.title);
	const interestChanged = interestAfter !== interestBefore;
	const typeAfter = normalizeType(formValues.type);
	const typeChanged = typeAfter !== normalizeType(transaction.type);

	if (!transaction.interestPairId) {
		// An interest edit Both -> single on an unpaired row is a split: the server creates the
		// counterpart leg (a seller-to-seller self-transfer on a TRANSFER; the CAP-2011 twins on a
		// REDEMPTION/CANCELLATION, replacing this row) and links the pair.
		if (interestBefore === BOTH && interestAfter !== BOTH && isSplittableType(typeAfter)) {
			return {
				kind: 'splitPreview',
				title: t('transactions.interest_pair.split_preview.title'),
				message: splitPreviewMessage(typeAfter, interestAfter, formValues.cost, t),
			};
		}
		return null;
	}

	const sibling = transaction.interestPairSibling;
	if (!sibling) {
		// CAP-2033: a one-sided bulk delete is a supported way to remove one leg of a redemption
		// pair, so a soft-deleted sibling is a user-made state with a known recovery — say that,
		// not "broken". The grid flags the same state as PAIR_LEG_MISSING.
		if (transaction.transactionError === 'PAIR_LEG_MISSING') {
			return {
				kind: 'blocked',
				title: t('transactions.interest_pair.sibling_deleted.title'),
				message: t('transactions.interest_pair.sibling_deleted.message'),
				offerDeletePair: false,
			};
		}
		// The row names a pair whose other leg cannot be resolved at all. The server degrades to an
		// unpaired edit rather than failing, but no edit made here can reach the missing rail, so
		// the user is told the record is broken instead of being left to save into it.
		return {
			kind: 'blocked',
			title: t('transactions.interest_pair.broken.title'),
			message: t('transactions.interest_pair.broken.message'),
			// There is no pair left to delete, and deleting this leg alone is what would cement the
			// half-pair state. Support has to repair the link.
			offerDeletePair: false,
		};
	}
	const siblingInterest = normalizeInterest(sibling.title);

	// CAP-2035: Both is the merge back to one row — the inverse of the split — so it is answered
	// before the rules that fix a single-title leg's interest.
	if (interestChanged && interestAfter === BOTH) {
		return {
			kind: 'mergePreview',
			title: t('transactions.interest_pair.merge_preview.title'),
			message: mergePreviewMessage(!!sibling.isSelf, t),
		};
	}

	if (interestChanged && isPairSyncType(transaction.type)) {
		return {
			kind: 'blocked',
			title: t('transactions.interest_pair.blocked_interest.title'),
			message: t('transactions.interest_pair.blocked_interest.message'),
			offerDeletePair: true,
		};
	}
	if (interestChanged && interestAfter === siblingInterest) {
		return {
			kind: 'blocked',
			title: t('transactions.interest_pair.blocked_collision.title'),
			// The rule is symmetric, so there is no order that gets the two legs swapped: retitling
			// the linked leg first collides with THIS leg by the same check. Deleting the pair is
			// the only way through, so the dialog offers it rather than suggesting an impossible
			// sequence — which is what the copy used to do.
			message: t('transactions.interest_pair.blocked_collision.message'),
			offerDeletePair: true,
		};
	}
	// CAP-2034: a paired leg's type change applies to both legs, so the pair keeps describing one
	// operation. Only one direction is refused — a redemption records a holder giving shares up, and
	// an issue is minted by the issuer, so converting one into the other inverts what it is about.
	const ownType = normalizeType(transaction.type);
	if (
		typeChanged &&
		typeAfter === TransactionType.ISSUE &&
		(ownType === TransactionType.REDEMPTION || ownType === TransactionType.CANCELLATION)
	) {
		return {
			kind: 'blocked',
			title: t('transactions.interest_pair.blocked_redeem_to_issue.title'),
			message: t('transactions.interest_pair.blocked_redeem_to_issue.message'),
			offerDeletePair: true,
		};
	}

	const syncKind = isPairSyncType(transaction.type);
	const fieldsToSync: string[] = [];
	if (typeChanged) {
		fieldsToSync.push(t('transactions.interest_pair.field.type'));
		// The server moves the linked leg's counterparty so it stays valid as its new type: an issue
		// is minted by the issuer, a redemption returns to it.
		if (typeAfter === TransactionType.ISSUE) {
			fieldsToSync.push(t('transactions.interest_pair.field.seller'));
		}
		if (
			typeAfter === TransactionType.REDEMPTION ||
			typeAfter === TransactionType.CANCELLATION
		) {
			fieldsToSync.push(t('transactions.interest_pair.field.buyer'));
		}
		// A transfer pins neither side to the issuer, so whichever side the old type pinned there
		// follows the edited leg onto the sibling.
		if (typeAfter === TransactionType.TRANSFER) {
			if (ownType === TransactionType.ISSUE) {
				fieldsToSync.push(t('transactions.interest_pair.field.seller'));
			}
			if (
				ownType === TransactionType.REDEMPTION ||
				ownType === TransactionType.CANCELLATION
			) {
				fieldsToSync.push(t('transactions.interest_pair.field.buyer'));
			}
		}
	}
	const dateChanged =
		!!formValues.date &&
		new Date(formValues.date).getTime() !== new Date(transaction.date).getTime();
	if (syncKind && dateChanged) {
		fieldsToSync.push(t('transactions.interest_pair.field.date'));
	}
	if (syncKind && Number(formValues.units) !== Number(transaction.units)) {
		fieldsToSync.push(t('transactions.interest_pair.field.units'));
	}
	if (formValues.securityId !== transaction.securityId) {
		fieldsToSync.push(t('transactions.interest_pair.field.security'));
	}
	const toMipChanged = (formValues.toMipId ?? null) !== (transaction.toMipId ?? null);
	if (toMipChanged && !sibling.programmeManaged && sibling.hasBuyerHolding) {
		fieldsToSync.push(t('transactions.interest_pair.field.buyer_holding'));
	}

	if (fieldsToSync.length > 0) {
		return {
			kind: 'confirm',
			title: t('transactions.interest_pair.confirm.title'),
			message: t('transactions.interest_pair.confirm.message', {
				fields: listFields(fieldsToSync),
			}),
		};
	}

	return null;
};
